import { ColorPicker } from "@/components/ColorPicker/colorPicker";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import { Input } from "@/shadcn/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select";
import { NewTaskTag } from "@/types";
import { colorIsDarkAdvanced, validateHexColor } from "@/utils/utils";
import {
  Check,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Save,
} from "lucide-react";
import React, { useContext, useEffect, useState } from "react";

type CreateTaskTagsProps = {
  id: string;
  taskTags: NewTaskTag[] | [];
  handleFieldTags(newTag: NewTaskTag): void;
  createTagAllowed: boolean;
  createTagOpen?: React.MutableRefObject<boolean>;
  side?: "top" | "right" | "bottom" | "left" | undefined;
  align?: "start" | "center" | "end" | undefined;
  modal?: boolean;
};

const CreateTaskTags: React.FC<CreateTaskTagsProps> = ({
  id,
  taskTags,
  createTagAllowed,
  createTagOpen,
  handleFieldTags,
  side,
  align,
  modal = false,
}) => {
  const { userTags } = useContext(AppContext);

  const [newTag, setNewTag] = useState<NewTaskTag | null>(null);
  const [openTags, setOpenTags] = useState(false);
  const [allJoinedTags, setAllJoinedTags] = useState<
    Array<{
      id: number;
      category: string;
      tags: {
        name: string;
        color: string;
      }[];
    }>
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  useEffect(() => {
    // Join userTags and taskTags

    const allTags: Array<{
      id: number;
      category: string;
      tags: {
        name: string;
        color: string;
      }[];
    }> = [];

    userTags.forEach((tagCat) => {
      const tags = tagCat.tags.map((tag) => {
        return { name: tag.name, color: tag.color };
      });
      allTags.push({ id: tagCat.id, category: tagCat.category, tags });
    });

    taskTags.forEach((tag) => {
      const tagCat = allTags.find((t) => t.id === tag.category_id);
      if (tagCat) {
        const tagExists = tagCat.tags.some((t) => t.name === tag.tag);
        if (!tagExists) {
          tagCat.tags.push({ name: tag.tag, color: tag.color });
        }
      } else {
        allTags.push({
          id: tag.category_id,
          category: "Unknown",
          tags: [{ name: tag.tag, color: tag.color }],
        });
      }
    });

    setAllJoinedTags(allTags);
    setFilteredTags(allTags);
  }, [userTags, taskTags]);

  function createNewTagName(tag: string) {
    if (!newTag) return;
    setNewTag({ ...newTag, tag });
  }

  function createNewTagColor(color: string) {
    if (!newTag) return;
    setNewTag({ ...newTag, color });
  }

  function createTagCategory(category_id: string) {
    if (!newTag) return;
    if (!category_id || category_id === "-1" || category_id === "0") return;
    let intcategory_id = 0;
    try {
      intcategory_id = Number(category_id);
    } catch (error) {
      return;
    }
    if (isNaN(intcategory_id) || intcategory_id === 0) return;

    setNewTag({ ...newTag, category_id: intcategory_id });
  }

  function saveNewTag() {
    if (!newTag) return;
    console.log(newTag);
    if (newTag.tag === "") return;
    if (newTag.category_id === 0) return;
    if (newTag.color === "" || !validateHexColor(newTag.color)) return;

    handleFieldTags(newTag);
    setNewTag(null);
  }

  // Tag filtering
  function toggleCategory(categoryID: number) {
    console.log(categoryID);
    setSelectedCategories((prev) => {
      console.log(prev);
      return prev.includes(categoryID)
        ? prev.filter((id) => id !== categoryID)
        : [...prev, categoryID];
    });
  }

  const [searchInput, setSearchInput] = useState<string>("");
  // Filter tags by category and input
  useEffect(() => {
    console.log("selectedCategories", selectedCategories);
    if (selectedCategories.length === 0) {
      setFilteredTags(allJoinedTags);
      return;
    }

    // Filter by category
    setFilteredTags(
      allJoinedTags.filter((tagCat) => selectedCategories.includes(tagCat.id))
    );
  }, [
    selectedCategories,
    allJoinedTags,
    searchInput,
    userTags,
    taskTags,
    newTag,
  ]);

  const [filteredTags, setFilteredTags] = useState(allJoinedTags);
  useEffect(() => {
    console.log(selectedCategories);
  }, [selectedCategories]);

  useEffect(() => {
    if (createTagOpen) {
      createTagOpen.current = openTags;
    }
  }, [openTags, createTagOpen]);

  useEffect(() => {
    console.log(allJoinedTags);
  }, [allJoinedTags]);

  useEffect(() => {
    console.log(filteredTags);
  }, [filteredTags]);

  return (
    <Popover
      open={openTags}
      onOpenChange={setOpenTags}
      modal={modal}
      key={"popover" + id}
      // placement="bottom-start"
    >
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={openTags}
          className="w-full justify-between"
        >
          {taskTags.length > 0 ? `${taskTags.length} selected` : "Select Tags"}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full p-0"
        side={side ?? undefined}
        align={align ?? undefined}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandList>
            <CommandGroup>
              {createTagAllowed && (
                <CommandItem
                  onSelect={() => {
                    setNewTag(
                      newTag
                        ? null
                        : { tag: "", color: "#dc2626", category_id: 0 }
                    );
                  }}
                  // onMouseDown={(e) => e.stopPropagation()}
                  // onClick={(e) => e.stopPropagation()}
                  className="flex justify-between"
                >
                  Create new tag
                  {newTag ? (
                    <ChevronUp className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  ) : (
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  )}
                </CommandItem>
              )}

              {newTag && (
                <CommandItem className="pb-2">
                  <div className="flex flex-col gap-2 w-full">
                    <Select
                      onValueChange={(value) => {
                        createTagCategory(value);
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Select a category</SelectLabel>
                          {userTags.map((tagCat) => (
                            <SelectItem
                              key={tagCat.id}
                              value={tagCat.id.toString()}
                            >
                              {tagCat.category}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    <div className="flex flex-row gap-2">
                      <Input
                        value={newTag.tag}
                        onChange={(e) => {
                          console.log(e.target.value);
                          createNewTagName(e.target.value);
                        }}
                        placeholder="Tag name"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        maxLength={64}
                      />
                      <ColorPicker
                        value={newTag.color}
                        onChange={(color) => {
                          createNewTagColor(color);
                        }}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size={"sm"}
                      onClick={() => {
                        saveNewTag();
                      }}
                      disabled={
                        newTag.tag === "" ||
                        newTag.color === "" ||
                        !validateHexColor(newTag.color) ||
                        newTag.category_id === 0
                      }
                    >
                      <Save className="h-4" /> Save tag
                    </Button>
                  </div>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>

        {/* Adding Removing Tags */}
        <Command>
          <CommandInput
            placeholder="Search tags..."
            value={searchInput}
            onInput={(e) => setSearchInput(e.currentTarget.value)}
          />
          <CommandList>
            {/* Filter by category */}
            <div className="flex flex-wrap gap-2 p-2">
              {userTags.map((tagCat) => (
                <Button
                  key={tagCat.id}
                  variant={
                    selectedCategories.includes(tagCat.id)
                      ? "default"
                      : "outline"
                  }
                  size="sm"
                  onClick={(e) => {
                    toggleCategory(tagCat.id);

                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  {tagCat.category}
                </Button>
              ))}
            </div>
            <ScrollArea
              // TODO: Fix scroll area properly.
              onWheel={(e) => e.stopPropagation()}
              className={`${newTag ? "h-[80px]" : "h-[200px]"}`}
            >
              {/* <CommandEmpty>No tags found.</CommandEmpty> */}
              {/* Display filtered tags */}
              {filteredTags.map((tagCat) => (
                <CommandGroup
                  key={tagCat.id}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <div className="ps-2">{tagCat.category}</div>
                  {tagCat.tags.map((tag, index) => (
                    <div
                      key={index}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <CommandItem
                        value={tag.name}
                        onSelect={() => {
                          handleFieldTags({
                            tag: tag.name,
                            color: tag.color,
                            category_id: tagCat.id,
                          });
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                        className="p-0.5"
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${
                            taskTags.some((t) => t.tag === tag.name)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        />
                        <span
                          className={`py-1 px-2 rounded`}
                          style={{
                            backgroundColor: tag.color,
                            color: colorIsDarkAdvanced(tag.color)
                              ? "white"
                              : "black",
                          }}
                        >
                          {tag.name}
                        </span>
                      </CommandItem>
                    </div>
                  ))}
                </CommandGroup>
              ))}
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CreateTaskTags;
